import type { PersonAttribute } from '@vigeo/shared';

/**
 * The questions behind a person's attributes, in HR's words.
 *
 * `satisfies Record<PersonAttribute, AttributeQuestion>` is the guard: adding an attribute
 * to the closed vocabulary in packages/shared is a compile error here until somebody writes
 * the question a human is actually being asked. Without it a new attribute would silently
 * become a requirement nobody can ever trigger — which is exactly the bug this feature
 * exists to fix.
 */
export interface AttributeQuestion {
  /** What HR is asked. Never the attribute key. */
  readonly question: string;
  /** Why it is being asked, and what turning it on will add to the checklist. */
  readonly helpText: string;
}

export const attributeQuestions = {
  'provides-patient-services': { question: 'Provides patient care or services, including remote services?', helpText: 'Requires initial competency for the actual duties. Clinical roles retain this requirement even if this answer is No.' },
  'occupational-blood-exposure': { question: 'Occupational exposure to blood or potentially infectious material?', helpText: 'Use the actual exposure assessment. This determines the Hepatitis B education and free offer/response pathway.' },
  'transports-patients': { question: 'Transports patients as an assigned duty?', helpText: 'Patient transport requires a driving record check at hire and annually. Ordinary business driving still requires a license and applicable insurance.' },
  'requires-respirator': { question: 'Required to use a respirator for assigned duties?', helpText: 'Adds medical clearance, fit testing and training. Leave unknown until the exposure assessment is reviewed.' },
  'qualified-via-aide-registry': {
    question: 'Qualified through the state aide registry?',
    helpText:
      'Aides who qualified via the registry route must be listed in good standing, re-checked annually. Leaving this unanswered holds them at "Needs review".',
  },
  'provides-direct-care': {
    question: 'Enters patients’ homes?',
    helpText: 'Direct patient care brings CPR and the TB screening requirements with it.',
  },
  'holds-professional-licence': {
    question: 'Holds a professional licence?',
    helpText: 'Brings licence copy and primary-source verification requirements.',
  },
  'has-conviction-disclosure': {
    question: 'Disclosed a conviction?',
    helpText: 'Adds the case-by-case conviction review determination to their checklist.',
  },
  'had-exposure-event': {
    question: 'Had a bloodborne exposure incident?',
    helpText: 'Adds the OSHA exposure record, which is retained for 30 years after employment.',
  },
  'requires-fitness-for-duty': {
    question: 'Needs a fitness-for-duty clearance?',
    helpText: 'Adds the return-to-work clearance and supporting medical documentation.',
  },
  'designated-aide-trainer': {
    question: 'Designated as an aide trainer?',
    helpText: 'Only RNs designated as aide trainers carry the trainer experience requirement.',
  },
  'delivers-medicaid-services': {
    question: 'Delivers Medicaid services?',
    helpText: 'Adds the electronic visit verification policy acknowledgment.',
  },
} as const satisfies Record<PersonAttribute, AttributeQuestion>;

/**
 * The three states an answer can be in.
 *
 * `unanswered` is NOT a third flavour of no, and collapsing it into one would undo the
 * whole tri-state design: an unanswered question on a blocking requirement holds a person
 * at "Needs review", where a "no" clears them. A plain checkbox cannot express that.
 */
export const attributeAnswers = ['yes', 'no', 'unanswered'] as const;
export type AttributeAnswer = (typeof attributeAnswers)[number];

export const answerLabels: Record<AttributeAnswer, string> = {
  yes: 'Yes',
  no: 'No',
  unanswered: 'Not answered',
};

export function answerOf(value: string | undefined): AttributeAnswer {
  if (value === 'yes') return 'yes';
  if (value === 'no') return 'no';
  return 'unanswered';
}
